import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useProfile } from 'hooks';
import { RoleMap } from 'hooks/useGetRole';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { OrganizationMember, useOrganizationsApi } from 'services/api';
import { PeoplePagination } from '../PeoplePagination';
import { PeopleSearchInput } from '../PeopleSearchInput';
import { MemberDetailBottomSheet } from './MemberDetailBottomSheet';
import { MemberRosterDesktopTable } from './MemberRosterDesktopTable';
import { MemberRosterMobileCards } from './MemberRosterMobileCards';
import { RemoveMemberModal } from './RemoveMemberModal';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export interface MemberRosterSubTabProps {
  organizationId: string;
}

export function MemberRosterSubTab ({
  organizationId,
}: MemberRosterSubTabProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const organizationsApi = useOrganizationsApi();
  const { enqueueSnackbar } = useSnackbar();
  const { profile } = useProfile();

  const canRemove = useMemo(() => {
    if (!profile) return false;
    if (profile.role.includes(RoleMap['executive-admin'])) return true;
    const isOrgAdmin = (profile.adminOrganizations ?? []).some(
      org => org.id === organizationId,
    );
    return profile.role.includes(RoleMap.admin) && isOrgAdmin;
  }, [profile, organizationId]);

  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [sheet, setSheet] = useState<OrganizationMember | null>(null);
  const [removeTarget, setRemoveTarget] = useState<OrganizationMember | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const res = await organizationsApi.listOrganizationMembers(organizationId, {
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch || undefined,
      });
      if (cancelled) return;
      if (res.success) {
        setMembers(res.value.members);
        setTotal(res.value.count);
      } else {
        enqueueSnackbar(res.error.message || 'Failed to load members', { variant: 'error' });
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [organizationId, page, debouncedSearch, reloadKey]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const onRemoveSuccess = async () => {
    setRemoveTarget(null);
    setSheet(null);
    setReloadKey(k => k + 1);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '24px', md: '16px' }, width: '100%' }}>
      <Box
        display="flex"
        alignItems="center"
        sx={{ width: '100%', maxWidth: 1400 }}
      >
        <PeopleSearchInput
          value={search}
          onChange={setSearch}
          fullWidth={isMobile}
        />
      </Box>

      {loading && members.length === 0 ? (
        <Box display="flex" alignItems="center" justifyContent="center" sx={{ py: '48px' }}>
          <CircularProgress size={28} />
        </Box>
      ) : !members.length ? (
        <Typography sx={{ mt: 2, color: 'text.secondary' }}>
          No members match your filters.
        </Typography>
      ) : isMobile ? (
        <MemberRosterMobileCards members={members} onRowPress={setSheet} />
      ) : (
        <MemberRosterDesktopTable
          members={members}
          canRemove={canRemove}
          onRemove={m => setRemoveTarget(m)}
        />
      )}

      {members.length > 0 ? (
        <PeoplePagination
          page={page}
          totalPages={totalPages}
          onPrev={() => setPage(p => Math.max(1, p - 1))}
          onNext={() => setPage(p => Math.min(totalPages, p + 1))}
        />
      ) : null}

      {isMobile ? (
        <MemberDetailBottomSheet
          open={!!sheet}
          member={sheet}
          canRemove={canRemove}
          onClose={() => setSheet(null)}
          onRemove={m => setRemoveTarget(m)}
        />
      ) : null}

      {removeTarget ? (
        <RemoveMemberModal
          open
          onClose={() => setRemoveTarget(null)}
          organizationId={organizationId}
          userId={removeTarget.userId}
          memberDisplayName={`${removeTarget.firstName} ${removeTarget.lastName}`.trim()}
          onSuccess={onRemoveSuccess}
        />
      ) : null}
    </Box>
  );
}
